import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import { cn } from '@/features/common/utilities/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indicator-info focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        solid: 'bg-primary-500',
        outline: 'bg-transparent border',
        link: 'px-0 hover:underline active:underline',
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      action: {
        primary:
          'bg-background-transparent border-primary-300 text-primary-600 hover:text-primary-600 active:text-primary-700',
        secondary:
          'bg-secondary-500 border-secondary-300 hover:bg-transparent hover:border-secondary-400 active:bg-transparent active:border-secondary-500 text-secondary-600 hover:text-secondary-600 active:text-secondary-700',
        positive:
          'bg-success-500 border-success-300 hover:bg-transparent hover:border-success-400 active:bg-success-700 active:border-success-500 text-success-600 hover:text-success-600 active:text-success-700',
        negative:
          'bg-error-500 border-error-300 hover:bg-error-600 hover:border-error-400 active:bg-error-700 active:border-error-500 text-error-600 hover:text-error-600 active:text-error-700',
        default: 'bg-transparent hover:bg-transparent active:bg-transparent',
      },
      size: {
        xs: 'px-3.5 h-8 text-xs',
        sm: 'px-4 h-9 text-sm',
        md: 'px-5 h-10 text-base',
        lg: 'px-6 h-11 text-lg',
        xl: 'px-7 h-12 text-xl',
      },
    },
    compoundVariants: [
      {
        variant: 'solid',
        action: ['primary', 'secondary', 'positive', 'negative'],
        className: 'text-white hover:text-white active:text-white',
      },
      {
        variant: 'outline',
        action: ['primary', 'secondary', 'positive', 'negative'],
        className:
          'text-primary-500 hover:text-primary-500 active:text-primary-500 bg-transparent hover:bg-transparent active:bg-transparent',
      },
      {
        variant: 'link',
        action: ['primary', 'secondary', 'positive', 'negative'],
        className: 'px-0',
      },
    ],
    defaultVariants: {
      variant: 'solid',
      action: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const t = useTranslations();
    const Comp = asChild ? Slot : 'button';

    const translatedChildren =
      typeof children === 'string' ? t(children) : children;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}>
        {translatedChildren}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
